import React, { useEffect, useState } from 'react';
import { CircularProgressbarWithChildren, buildStyles } from 'react-circular-progressbar';
import 'react-circular-progressbar/dist/styles.css';
import {
    LineChart, Line, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid
} from 'recharts';

//Setup for report visuals
const tabLabels = {
  motivation: "Motivation",
  skills: "Leadership Skills",
  style: "Leadership Style",
  emotionalIntelligence: "Emotional Intelligence"
};

const tabMapping = {
  motivation: ['autonomyRating', 'relatednessRating', 'competenceRating'],
  skills: ['microskillsFeedbackRating', 'activeListeningRating', 'problemSolvingRating'],
  style: ['styleSummaryRating', 'focusedRating', 'inspiringRating'],
  emotionalIntelligence: ['emSelfRating', 'socialUnderstanding', 'emOtherRating']
};

const scoreColors = (val) => {
  if (val <= 2.5) return 'red';
  if (val <= 5.5) return 'yellow';
  return 'green';
};

const labelMap = {
    autonomyRating: "Autonomy",
    relatednessRating: "Relatedness",
    competenceRating: "Competence",
    microskillsFeedbackRating: "Feedback",
    activeListeningRating: "Active Listening",
    problemSolvingRating: "Facilitating Problem Solving",
    styleSummaryRating: "Overall Style",
    focusedRating: "Focused Style",
    inspiringRating: "Inspired Style",
    emSelfRating: "Manage Self Emotion",
    socialUnderstanding: "Understand Others Emotion",
    emOtherRating: "Manage Others Emotion",
  };

const metricDisplayNames = {
    autonomy: "Autonomy",
    relatedness: "Relatedness",
    competence: "Competence",
    microskillsFeedback: "Feedback",
    problemSolving: "Facilitating Problem Solving",
    styleSummary: "Leadership Style Summary",
    styleFocused: "Focused Style",
    styleInspired: "Inspired Style",
    emotionalSelf: "Manage Self Emotion",
    socialUnderstanding: "Understand Others Emotion",
    emotionalOther: "Manage Other's Emotion",
};

const metricKeyMap = {
    autonomy: "autonomyRating",
    relatedness: "relatednessRating",
    competence: "competenceRating",
    microskillsFeedback: "microskillsFeedbackRating",
    problemSolving: "problemSolvingRating",
    styleSummary: "styleSummaryRating",
    styleFocused: "focusedRating",
    styleInspired: "inspiringRating",
    emotionalSelf: "emSelfRating",
    socialUnderstanding: "socialUnderstanding",
    emotionalOther: "emOtherRating",
  };
  
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const metricKey = payload[0].dataKey;
      const displayName = labelMap[metricKey] || metricKey;
      const value = payload[0].value;
  
      return (
        <div className="bg-white border border-gray-300 rounded shadow p-2 text-sm">
          <p className="font-semibold">{displayName}</p>
          <p>{new Date(label).toLocaleDateString()}</p>
          <p>Score: {value.toFixed(1)}</p>
        </div>
      );
    }
  
    return null;
  };

const GroupReportSummary = ({ report }) => {
  const [activeTab, setActiveTab] = useState("motivation");

  const calcAvg = (keys) => {
    const nums = keys.map(k => parseFloat(report.ratings[k])).filter(n => !isNaN(n));
    return nums.length ? (nums.reduce((a, b) => a + b, 0) / nums.length).toFixed(1) : "N/A";
  };

  return (
    <div className="mt-6 bg-white p-6 rounded shadow border border-gray-200">
        {/* Report Section Header */}
        <h3 className="text-xl font-bold text-gray-800 mb-4">Conversation Feedback Summary</h3>
      
        {/* Executive Summary */}
        <div className="mb-6 border-l-4 border-yellow-400 bg-yellow-50 p-4 rounded">
            <p className="italic text-sm text-yellow-800">{report.executiveSummary}</p>
        </div>

        {/* Tabs */}
        <div className="flex mb-4">
            {Object.entries(tabLabels).map(([key, label]) => {
            const avg = calcAvg(tabMapping[key]);
            const color = scoreColors(avg);
            return (
                <button
                key={key}
                className={`flex-1 px-3 py-2 text-sm font-medium text-center border rounded-t ${
                    activeTab === key
                    ? 'bg-white border-b-white text-gray-800 shadow'
                    : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                }`}
                onClick={() => setActiveTab(key)}
                >
                <div className="w-10 h-10 mx-auto mb-1">
                    <CircularProgressbarWithChildren
                    value={avg}
                    maxValue={7}
                    styles={buildStyles({
                        pathColor: color === 'red' ? '#EF4444' : color === 'yellow' ? '#EAB308' : '#22C55E',
                        trailColor: "#e5e7eb"
                    })}
                    >
                    <div className="text-sm font-bold">{avg}</div>
                    </CircularProgressbarWithChildren>
                </div>
                {label}
                </button>
            );
            })}
        </div>

        {/* Tab Content */}
        <div className="border rounded-b border-t-0 p-4">
            <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {tabMapping[activeTab].map((key) => {
                    const score = parseInt(report.ratings[key]);
                    const fillColor = score <= 2 ? 'bg-red-500' : score <= 5 ? 'bg-yellow-400' : 'bg-green-500';

                    return (
                        <li key={key} className="flex flex-col items-center text-sm bg-gray-50 border rounded px-4 py-4 shadow-sm">
                        <span className="text-center font-semibold text-gray-700 mb-2">{labelMap[key] ?? key}</span>
                        <div className="flex gap-1">
                            {[...Array(7)].map((_, i) => (
                            <span
                                key={i}
                                className={`w-3 h-3 rounded-full ${i < score ? fillColor : 'bg-gray-300'}`}
                            />
                            ))}
                        </div>
                        </li>
                    );
                })}
            </ul>
        </div>
    </div>
  );
};


const GroupAdminPage = () => {
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [groupAdminFor, setGroupAdminFor] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState('');
  const [conversations, setConversations] = useState([]);
  const [loadingConversations, setLoadingConversations] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [loadingReport, setLoadingReport] = useState(false);
  const [selectedConversationId, setSelectedConversationId] = useState(null);
  const [effectivenessScore, setEffectivenessScore] = useState(null);
  const [selectedMetric, setSelectedMetric] = useState('autonomy');
  const [trendData, setTrendData] = useState([]);
  const [xAxisMode, setXAxisMode] = useState("relative"); // "relative" or "equal"

  // On mount: load groups and fetch users
  useEffect(() => {
    const groups = JSON.parse(localStorage.getItem("groupAdminFor") || "[]");
    setGroupAdminFor(groups);

    if (groups.length > 0) {
      setSelectedGroup(groups[0]);
    }
  }, []);

  // When group changes, fetch users
  useEffect(() => {
    const fetchUsers = async () => {
      if (!selectedGroup) return;

      try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/group-admin/users?group=${selectedGroup}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });

        if (!response.ok) throw new Error("Failed to fetch users");

        const data = await response.json();
        setUsers(data.users);
        setSelectedUser(null); // Clear selected user on group change
      } catch (err) {
        console.error("Error fetching group users:", err);
        setUsers([]);
      }
    };

    fetchUsers();
  }, [selectedGroup]);

  //when user changes pull conversation data
  useEffect(() => {
    const fetchConversations = async () => {
      if (!selectedUser) return;
  
      setLoadingConversations(true);

      try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/group-admin/conversations?userId=${selectedUser.id}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });
  
        if (!response.ok) throw new Error("Failed to fetch conversations");
  
        const data = await response.json();
        console.log('conversation data returned:', data);

        setConversations(data.conversations);
        setEffectivenessScore(data.effectivenessScore);
      } catch (err) {
        console.error("Error fetching conversations:", err);
        setConversations([]);
      } finally {
      setLoadingConversations(false);
      }
    };
  
    fetchConversations();
  }, [selectedUser]);
  
    // Clear report when selected user changes
    useEffect(() => {
        setSelectedReport(null);
        setSelectedConversationId(null);
    }, [selectedUser]);

    //Endpoint to pull feeedback data for trends chart
    useEffect(() => {
        const fetchTrends = async () => {
          if (!selectedUser) return;
      
          try {
            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/group-admin/trend-data?userId=${selectedUser.id}`, {
              headers: {
                Authorization: `Bearer ${localStorage.getItem("token")}`,
              },
            });
            if (!res.ok) throw new Error("Failed to fetch trend data");
            const data = await res.json();
            console.log('trend data:', data);
            setTrendData(data.data || []);
          } catch (err) {
            console.error("Trend fetch error:", err);
            setTrendData([]);
          }
        };
      
        fetchTrends();
      }, [selectedUser]);

  //function to call report data for selected conversation
  const handleConversationClick = async (conv) => {
    if (!conv.hasReport) return;
  
    setLoadingReport(true);
    setSelectedConversationId(conv.id);

    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/group-admin/report?conversationId=${conv.id}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        }
      });
  
      if (!res.ok) throw new Error('Failed to fetch report');
      const data = await res.json();
      setSelectedReport({ ...data, conversationId: conv.id });
    } catch (err) {
      console.error('Report fetch error:', err);
      setSelectedReport(null);
    } finally {
      setLoadingReport(false);
    }
  };
  
  return (
    <div className="p-6 bg-gray-50 min-h-screen flex flex-col lg:flex-row gap-6">
      {/* Sidebar Section */}
      <div className="w-full min-w-[230px] lg:w-1/4 bg-white border rounded shadow p-4">
        <h2 className="text-xl font-semibold mb-4">Group Users</h2>

        {/* Group Selector */}
        {groupAdminFor.length > 1 && (
          <select
            value={selectedGroup}
            onChange={(e) => setSelectedGroup(e.target.value)}
            className="mb-4 p-2 border rounded w-full"
          >
            {groupAdminFor.map(group => (
              <option key={group} value={group}>
                {group}
              </option>
            ))}
          </select>
        )}

        {users.length === 0 ? (
          <p className="text-gray-500">No users found for this group.</p>
        ) : (
          <ul className="space-y-2">
            {users.map(user => (
              <li
                key={user.id}
                onClick={() => setSelectedUser(user)}
                className={`p-3 border rounded cursor-pointer hover:bg-gray-100 ${
                  selectedUser?.id === user.id ? 'bg-blue-100' : ''
                }`}
              >
                <div className="font-medium">{user.firstName} {user.lastName}</div>
                <div className="text-sm text-gray-500">{user.email}</div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* User Detail Panel */}
      <div className="w-full lg:w-2/3 bg-white border rounded shadow p-6">
        {selectedUser ? (
          <>
            <h2 className="text-2xl font-bold mb-2">{selectedUser.firstName}'s Progress</h2>
            <p className="text-gray-600 mb-6">Here you will see their conversation metrics, reports, and score trends.</p>

            {/* Summary Measures */}
            <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="p-4 bg-blue-50 rounded shadow">
                    <p className="text-sm text-gray-600">Total Conversations</p>
                    {loadingConversations ? (
                    <div className="h-6 bg-blue-100 rounded w-12 animate-pulse"></div>
                    ) : (
                    <p className="text-2xl font-semibold text-blue-800">{conversations.length}</p>
                    )}
                </div>
                <div className="p-4 bg-green-50 rounded shadow">
                    <p className="text-sm text-gray-600">Latest Effectiveness Score</p>
                    {loadingConversations ? (
                    <div className="h-6 bg-green-100 rounded w-16 animate-pulse"></div>
                    ) : (
                    <p className="text-2xl font-semibold text-green-800">
                        {effectivenessScore !== null ? effectivenessScore.toFixed(1) : 'N/A'}
                    </p>
                    )}
                </div>
            </div>

            {/* Leadership Trend Graph */}
            <div className="bg-white rounded shadow p-4 mb-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Leadership Trends Over Time</h3>

                {/* Metric Selector */}
                <div className="flex flex-wrap gap-2 mb-4">
                    {Object.keys(metricDisplayNames).map((key) => (
                    <button
                        key={key}
                        onClick={() => setSelectedMetric(key)}
                        className={`px-3 py-1 text-xs font-medium rounded shadow-sm ${
                        selectedMetric === key
                            ? "bg-purple-600 text-white scale-110"
                            : "bg-white text-gray-800 border border-gray-300 hover:bg-gray-100"
                        }`}
                    >
                        {metricDisplayNames[key]}
                    </button>
                    ))}
                </div>

                <div className="mb-4 flex items-center justify-center space-x-2">
                    <span className="text-sm font-medium text-gray-700">X-Axis Mode:</span>
                    <div className="inline-flex items-center bg-gray-200 rounded-lg p-1 shadow-inner">
                        <button
                        onClick={() => setXAxisMode("relative")}
                        className={`px-4 py-1 rounded-l-lg text-sm font-medium transition ${
                            xAxisMode === "relative"
                            ? "bg-purple-600 text-white"
                            : "text-gray-700 hover:bg-gray-100"
                        }`}
                        >
                        Time Scale
                        </button>
                        <button
                        onClick={() => setXAxisMode("equal")}
                        className={`px-4 py-1 rounded-r-lg text-sm font-medium transition ${
                            xAxisMode === "equal"
                            ? "bg-purple-600 text-white"
                            : "text-gray-700 hover:bg-gray-100"
                        }`}
                        >
                        Even Spacing
                        </button>
                    </div>
                </div>


                {/* Graph */}
                <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={trendData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                        dataKey={xAxisMode === "equal" ? "index" : "date"}
                        type={xAxisMode === "equal" ? "category" : "number"}
                        domain={xAxisMode === "equal" ? undefined : ["dataMin", "dataMax"]}
                        tickFormatter={(value, index) => {
                            const dateValue =
                            xAxisMode === "equal"
                                ? trendData[index]?.date
                                : value;

                            if (!dateValue || isNaN(dateValue)) return "";
                            return new Date(dateValue).toLocaleDateString();
                        }}
                    />
                    <YAxis domain={[1, 7]} ticks={[1, 2, 3, 4, 5, 6, 7]} />
                    <Tooltip content={<CustomTooltip />} />
                    <Line
                        key={metricKeyMap[selectedMetric]} // helps trigger animation properly
                        type="monotone"
                        dataKey={metricKeyMap[selectedMetric]}
                        stroke="#8884d8"
                        isAnimationActive={true}
                    />
                    </LineChart>
                </ResponsiveContainer>
            </div>

            {/* Conversation List */}
            <div className="mt-6">
                <h3 className="text-lg font-semibold mb-2">Conversations</h3>
                {loadingConversations ? (
                    <div className="flex justify-center items-center h-24">
                        <svg className="animate-spin h-6 w-6 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"></path>
                        </svg>
                    </div>
                ) : conversations.length === 0 ? (
                    <p className="text-gray-500">No conversations found for this user.</p>
                ) : (
                    <ul className="space-y-2 max-h-80 overflow-y-auto pr-1">
                        {conversations.map((conv) => (
                            <li
                                key={conv.id}
                                onClick={() => conv.hasReport && handleConversationClick(conv)}
                                className={`
                                    border p-3 rounded 
                                    ${conv.hasReport ? 'cursor-pointer hover:bg-blue-50' : 'cursor-default hover:bg-gray-100'} 
                                    ${selectedConversationId === conv.id ? 'bg-blue-100 border-blue-300' : 'bg-gray-50'}
                                `}
                            >
                                <div className="flex justify-between items-start">
                                    <div className="flex flex-col">
                                        <div className="flex items-center gap-2 flex-wrap">
                                        <p className="font-medium">{conv.title || 'Untitled Scenario'}</p>
                                        <span className="bg-purple-100 text-purple-800 text-xs font-semibold px-2 py-1 rounded-full">
                                            {conv.difficulty}
                                        </span>
                                        <p className="text-xs text-gray-500">
                                            {conv.createdAt && new Date(conv.createdAt._seconds * 1000).toLocaleDateString()}
                                        </p>
                                        </div>
                                        <p className="text-sm text-gray-600">Conversation length: {conv.length} turns</p>
                                    </div>

                                    {conv.hasReport && (
                                        <span className="text-green-600 font-bold text-sm">Report</span>
                                    )}
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            {/* Report Summary */}
            {loadingReport ? (
                <div className="flex justify-center items-center h-24 mt-6">
                    <svg className="animate-spin h-6 w-6 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"></path>
                    </svg>
                </div>
                ) : selectedReport && (
                <GroupReportSummary report={selectedReport} />
            )}

          </>
        ) : (
          <p className="text-gray-600 text-lg">Select a user to view their progress.</p>
        )}
      </div>
    </div>
  );
};

export default GroupAdminPage;
